// Lawn pest characteristics database for diagnosis feature

export interface PestCharacteristics {
  id: string
  name: string
  category: 'insect' | 'animal'
  damagePattern: string
  visualSigns: string[]
  affectedGrassTypes: string[]
  commonRegions: string[]
  severity: 'mild' | 'moderate' | 'severe'
  activeSeasons: string[]
  lifecycleInfo: string
  controlOptions: { cultural: string[]; chemical: string[]; organic: string[] }
  preventionTips: string[]
  diagnosticTest?: string
}

export const PEST_CHARACTERISTICS: Record<string, PestCharacteristics> = {
  'white-grubs-japanese-beetle': {
    id: 'white-grubs-japanese-beetle',
    name: 'White Grubs (Japanese Beetle)',
    category: 'insect',
    damagePattern: 'Irregular brown patches that lift like carpet; roots severed below soil surface',
    visualSigns: [
      'C-shaped white larvae 1/2 to 1 inch in soil',
      'Spongy, easily-lifted turf',
      'Increased bird, skunk, or raccoon digging',
      'Adult Japanese beetles on nearby plants'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Fine Fescue', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Central'],
    severity: 'severe',
    activeSeasons: ['late summer', 'fall'],
    lifecycleInfo: 'One-year cycle: adults lay eggs in summer, grubs feed on roots in fall, overwinter deep in soil, resume feeding in spring, pupate in early summer.',
    controlOptions: {
      cultural: ['Irrigate deeply to encourage deep root growth', 'Overseed damaged areas', 'Allow lawns to go dormant in summer to reduce egg-laying appeal'],
      chemical: ['Chlorantraniliprole (preventive, spring)', 'Trichlorfon (curative, fall)', 'Imidacloprid (preventive, early summer)'],
      organic: ['Milky spore (Bacillus popilliae), which works on Japanese beetle grubs only and does nothing to European chafer or other white grub species', 'Beneficial nematodes (Heterorhabditis bacteriophora)', 'Neem oil to deter adults']
    },
    preventionTips: ['Apply preventive grub control in late spring', 'Reduce irrigation in July to discourage egg laying', 'Maintain healthy turf to tolerate moderate populations'],
    diagnosticTest: 'Tug test: grab a handful of brown grass and pull. If the turf lifts like a carpet with no roots, grubs are likely present. Cut a 1-sq-ft section and count: threshold is 10+ grubs per sq ft.'
  },

  'white-grubs-european-chafer': {
    id: 'white-grubs-european-chafer',
    name: 'White Grubs (European Chafer)',
    category: 'insect',
    damagePattern: 'Large irregular dead areas, especially severe in spring; turf peels back easily',
    visualSigns: [
      'C-shaped white grubs in soil, 3/4 inch long',
      'Turf peels away from soil',
      'Intense animal digging (skunks, crows)',
      'Brown adult beetles swarming at dusk in late June'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Fine Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Pacific Northwest'],
    severity: 'severe',
    activeSeasons: ['spring', 'fall'],
    lifecycleInfo: 'One-year cycle similar to Japanese beetle but grubs feed earlier in spring and later in fall due to higher cold tolerance.',
    controlOptions: {
      cultural: ['Overseed damaged areas', 'Deep watering to promote root growth', 'Maintain thick turf'],
      chemical: ['Chlorantraniliprole (preventive)', 'Trichlorfon (curative)', 'Imidacloprid'],
      organic: ['Beneficial nematodes (Heterorhabditis bacteriophora)', 'Milky spore (Bacillus popilliae) does not control this species; it works on Japanese beetle grubs only']
    },
    preventionTips: ['Apply preventive grub control by early July', 'Maintain vigorous turf', 'Monitor for adult beetle flights in June'],
    diagnosticTest: 'Tug test: if turf lifts like a carpet, check soil for C-shaped grubs. Threshold: 5+ per sq ft.'
  },

  'chinch-bugs': {
    id: 'chinch-bugs',
    name: 'Chinch Bugs',
    category: 'insect',
    damagePattern: 'Irregular yellow patches expanding outward, often along driveways and sidewalks (heat-retaining edges)',
    visualSigns: [
      'Tiny black insects (1/6 inch) with white wing patches',
      'Nymphs are red-orange, turning black with age',
      'Damage starts in hot, sunny areas near pavement',
      'Grass yellows, then turns brown and dies'
    ],
    affectedGrassTypes: ['St. Augustinegrass', 'Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Zoysiagrass'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast', 'Northeast', 'Central'],
    severity: 'severe',
    activeSeasons: ['summer'],
    lifecycleInfo: 'Two to three generations per year in warm climates. Adults overwinter in thatch and plant debris. Peak damage occurs in July-August.',
    controlOptions: {
      cultural: ['Reduce thatch (chinch bugs harbor in thatch)', 'Water adequately during drought', 'Avoid excess nitrogen'],
      chemical: ['Bifenthrin', 'Trichlorfon', 'Clothianidin'],
      organic: ['Beauveria bassiana (biological insecticide)', 'Beneficial big-eyed bugs (natural predator)', 'Diatomaceous earth']
    },
    preventionTips: ['Keep thatch below 1/2 inch', 'Water during dry periods', 'Choose chinch bug-resistant St. Augustine cultivars (e.g., Floratam)'],
    diagnosticTest: 'Flotation test: cut both ends from a coffee can, push one end into the soil at the edge of damage, fill with water. Chinch bugs will float to the surface within 5 minutes.'
  },

  'sod-webworms': {
    id: 'sod-webworms',
    name: 'Sod Webworms',
    category: 'insect',
    damagePattern: 'Small irregular brown patches that grow together; grass blades are chewed off at soil level',
    visualSigns: [
      'Small tan moths flying in zigzag pattern at dusk',
      'Green to tan caterpillars (3/4 inch) in thatch',
      'Green frass (excrement pellets) in thatch layer',
      'Grass blades chewed off close to the ground'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Central', 'Great Lakes', 'Southeast'],
    severity: 'moderate',
    activeSeasons: ['summer'],
    lifecycleInfo: 'Two to three generations per year. Larvae feed at night, hiding in silk-lined tunnels in thatch during the day. Adult moths lay eggs at dusk.',
    controlOptions: {
      cultural: ['Reduce thatch to remove larval habitat', 'Water in the evening to irrigate applied treatments into thatch', 'Keep grass taller'],
      chemical: ['Bifenthrin', 'Chlorantraniliprole', 'Trichlorfon'],
      organic: ['Bacillus thuringiensis (Bt) applied at dusk', 'Beneficial nematodes', 'Spinosad']
    },
    preventionTips: ['Reduce thatch buildup', 'Monitor for zigzag moth flights', 'Maintain healthy turf to tolerate minor feeding'],
    diagnosticTest: 'Soap flush test: mix 2 tablespoons dish soap in 1 gallon water, pour over 1 sq ft of suspect area. Larvae will come to the surface within 10 minutes.'
  },

  'armyworms': {
    id: 'armyworms',
    name: 'Armyworms',
    category: 'insect',
    damagePattern: 'Rapidly expanding circular brown areas; entire sections of lawn can be consumed overnight',
    visualSigns: [
      'Green-brown striped caterpillars 1-2 inches long',
      'Large flocks of birds feeding on the lawn',
      'Grass blades chewed to the crown',
      'Advancing front of damage; caterpillars march in groups'
    ],
    affectedGrassTypes: ['Bermudagrass', 'Kentucky Bluegrass', 'Tall Fescue', 'Perennial Ryegrass', 'Zoysiagrass'],
    commonRegions: ['Southeast', 'South Central', 'Central', 'Mid-Atlantic'],
    severity: 'severe',
    activeSeasons: ['late summer', 'fall'],
    lifecycleInfo: 'Moths migrate northward in spring/summer. Multiple generations per year. Larvae feed voraciously for 2-3 weeks before pupating.',
    controlOptions: {
      cultural: ['Mow high to give grass reserves for recovery', 'Water and fertilize after damage to promote regrowth'],
      chemical: ['Bifenthrin', 'Chlorantraniliprole', 'Cyfluthrin'],
      organic: ['Bacillus thuringiensis (Bt) for young larvae', 'Spinosad', 'Beneficial nematodes']
    },
    preventionTips: ['Monitor for adult moths around outdoor lights', 'Inspect lawn in early morning when larvae are active', 'Act immediately; armyworms cause severe damage in days'],
    diagnosticTest: 'Soap flush test or inspect at dawn/dusk when caterpillars are actively feeding. Look for green pellets of frass and advancing damage front.'
  },

  'cutworms': {
    id: 'cutworms',
    name: 'Cutworms',
    category: 'insect',
    damagePattern: 'Small circular dead spots 1-3 inches across; individual plants cut off at soil level',
    visualSigns: [
      'Dull gray-brown caterpillars that curl into C-shape when disturbed',
      'Small circular bare spots on putting greens and short-cut turf',
      'Green frass pellets near damaged areas',
      'Damage most visible in short-mowed areas'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Bermudagrass', 'Perennial Ryegrass', 'Bentgrass'],
    commonRegions: ['All regions'],
    severity: 'mild',
    activeSeasons: ['spring', 'summer', 'fall'],
    lifecycleInfo: 'Multiple generations per year. Larvae feed at night, hiding in soil burrows during the day. Adults are drab brown moths.',
    controlOptions: {
      cultural: ['Mow regularly', 'Reduce thatch', 'Water in the morning'],
      chemical: ['Bifenthrin', 'Chlorantraniliprole', 'Lambda-cyhalothrin'],
      organic: ['Bacillus thuringiensis (Bt)', 'Beneficial nematodes', 'Spinosad']
    },
    preventionTips: ['Monitor for cutworm moth flights', 'Keep thatch minimal', 'Inspect short-mowed areas regularly'],
    diagnosticTest: 'Soap flush test: drench suspect areas with soapy water at dusk. Cutworms will surface from their burrows within minutes.'
  },

  'mole-crickets': {
    id: 'mole-crickets',
    name: 'Mole Crickets',
    category: 'insect',
    damagePattern: 'Soft, spongy turf with raised tunnels visible on the surface; grass dies from root disruption',
    visualSigns: [
      'Raised tunnels and mounds on soil surface',
      'Spongy feel when walking on turf',
      'Brown, velvety insects 1-2 inches long with shovel-like front legs',
      'Grass wilts and dies in irregular patterns'
    ],
    // Ordered most to least susceptible, per UF/IFAS IN1021: bahiagrass and
    // bermudagrass take the most damage, while centipedegrass, St. Augustine,
    // and zoysia are the least frequently injured. No species is immune.
    affectedGrassTypes: ['Bahiagrass', 'Bermudagrass', 'St. Augustinegrass', 'Centipede', 'Zoysiagrass'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast'],
    severity: 'severe',
    activeSeasons: ['spring', 'summer', 'fall'],
    lifecycleInfo: 'One generation per year. Adults tunnel and mate in spring. Nymphs hatch in June and feed through fall, causing most damage in late summer.',
    controlOptions: {
      cultural: ['Roll turf to compress tunnels', 'Irrigate to settle soil', 'Overseed damaged areas'],
      chemical: ['Bifenthrin', 'Imidacloprid', 'Fipronil (bait formulations)'],
      organic: ['Beneficial nematodes (Steinernema scapterisci)', 'Larra bicolor wasps (biological control)', 'Beauveria bassiana']
    },
    preventionTips: ['Monitor for adult flights in spring', 'Apply preventive control for nymphs in June-July', 'Maintain thick turf'],
    diagnosticTest: 'Soap flush test: pour soapy water (2 tbsp per gallon) over 4 sq ft. Mole crickets surface within 3-5 minutes. Check in the evening.'
  },

  'billbugs': {
    id: 'billbugs',
    name: 'Billbugs',
    category: 'insect',
    damagePattern: 'Irregular brown patches in mid-summer; grass stems easily break at crown with sawdust-like frass inside',
    visualSigns: [
      'Small snout-nosed beetles (1/4-3/8 inch) walking on pavement in spring',
      'Hollowed-out grass stems with sawdust-like frass',
      'Brown patches that don\'t respond to watering',
      'Legless white larvae in crown and root area'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Zoysiagrass', 'Bermudagrass'],
    commonRegions: ['Northeast', 'Great Lakes', 'Central', 'Mid-Atlantic'],
    severity: 'moderate',
    activeSeasons: ['summer'],
    lifecycleInfo: 'One generation per year. Adults overwinter in thatch and leaf litter. They emerge in spring, walk to new areas, and lay eggs in grass stems. Larvae feed from late spring through summer.',
    controlOptions: {
      cultural: ['Choose resistant grass cultivars (endophyte-enhanced)', 'Reduce thatch', 'Irrigate during drought'],
      chemical: ['Chlorantraniliprole (preventive)', 'Bifenthrin', 'Imidacloprid'],
      organic: ['Plant endophyte-enhanced grass varieties', 'Beneficial nematodes', 'Beauveria bassiana']
    },
    preventionTips: ['Plant endophyte-enhanced grass seed', 'Monitor for adults on sidewalks in spring', 'Apply preventive control by late May'],
    diagnosticTest: 'Stem pull test: grab individual grass stems and pull. If they break easily at the crown with sawdust-like frass visible, billbug larvae are present.'
  },

  'fire-ants': {
    id: 'fire-ants',
    name: 'Fire Ants',
    category: 'insect',
    damagePattern: 'Large dome-shaped mounds in lawn; surrounding grass may thin from soil displacement',
    visualSigns: [
      'Dome-shaped mounds up to 18 inches across',
      'Aggressive red-brown ants that sting when disturbed',
      'Multiple mounds across the lawn',
      'Bare soil on top of mounds'
    ],
    affectedGrassTypes: ['Bermudagrass', 'St. Augustinegrass', 'Zoysiagrass', 'Centipede', 'Bahiagrass'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast', 'Southwest'],
    severity: 'moderate',
    activeSeasons: ['spring', 'summer', 'fall'],
    lifecycleInfo: 'Colonies can persist for years. Queens produce thousands of eggs. Multiple queens per colony. New colonies formed by winged flights after rain.',
    controlOptions: {
      cultural: ['Broadcast bait treatments over entire yard', 'Individual mound treatments for immediate control', 'Maintain vigilance; re-treat new mounds'],
      chemical: ['Hydramethylnon bait', 'Spinosad bait', 'Acephate (individual mound drench)', 'Bifenthrin (broadcast)'],
      organic: ['Spinosad-based baits', 'Diatomaceous earth on mounds', 'Boiling water on individual mounds (limited effectiveness)']
    },
    preventionTips: ['Apply broadcast bait 2-3 times per year', 'Treat new mounds promptly', 'Monitor after rain events for new colonies'],
    diagnosticTest: 'Disturb the top of a suspect mound with a long stick from a step back, never with a hand or foot. Fire ants swarm out within seconds and run up the stick, and the mound has no single opening on top. Ant hills with a visible central hole are usually a different species.'
  },

  'moles': {
    id: 'moles',
    name: 'Moles',
    category: 'animal',
    damagePattern: 'Surface tunnels (raised ridges) and volcano-shaped mounds of excavated soil',
    visualSigns: [
      'Raised ridges of tunneling across the lawn',
      'Conical mounds of loose soil',
      'Spongy areas where tunnels have collapsed',
      'Disrupted root zones causing grass to yellow and die'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Fine Fescue', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['All regions'],
    severity: 'moderate',
    activeSeasons: ['spring', 'fall'],
    lifecycleInfo: 'Solitary except for a short late-winter breeding period. A mole may burrow as fast as 1 foot per minute making surface feeding tunnels. The main food is earthworms, supplemented with insect larvae and other soil invertebrates; moles rarely eat plant material. Peak surface activity in spring and fall.',
    controlOptions: {
      cultural: ['Roll or tamp tunnels to minimize lawn damage', 'Confirm an active main runway (flatten, mark, recheck in 24 hours) before any control', 'Keep lawn well-watered to compress soil'],
      chemical: ['Bromethalin worm-replica bait (positive reports only; more testing needed)'],
      organic: ['Scissor-jaw or harpoon traps (most effective method)', 'Castor oil repellents (no consistent research support)', 'Vibrating and electronic stakes (not proven effective)']
    },
    preventionTips: ['Scout for active main runways in spring and fall rather than treating on a calendar', 'Tamp lifted ridges back down and water the strip so roots regain soil contact', 'Trap promptly when activity is first noticed'],
    diagnosticTest: 'Step on a tunnel to flatten it and mark the location. Check after 24 hours: if the tunnel is re-raised, it\'s an active runway. Set traps on active runways.'
  },

  'voles': {
    id: 'voles',
    name: 'Voles',
    category: 'animal',
    damagePattern: 'Surface runways 1-2 inches wide in grass, with small round entry holes; bark stripped from trees',
    visualSigns: [
      'Narrow worn paths (runways) through grass',
      'Small round holes (about 1 to 2 inches) along runways, with no soil mound around them',
      'Grass clippings and droppings along runways',
      'Most damage visible after snow melt'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Fine Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Upper Midwest', 'Pacific Northwest'],
    severity: 'moderate',
    activeSeasons: ['fall', 'winter', 'spring'],
    lifecycleInfo: 'Prolific breeders, up to 10 litters per year. Populations cycle between high and low roughly every 3 to 6 years. Active year-round but damage peaks in winter under snow cover.',
    controlOptions: {
      cultural: ['Keep grass mowed short in fall', 'Remove ground cover and mulch near lawn edges', 'Clear snow from base of trees'],
      chemical: ['Zinc phosphide bait (restricted use in most states; certified applicator only)'],
      organic: ['Snap traps placed perpendicular to runways', 'Encourage predators (hawks, owls, snakes)', 'Hardware cloth barriers around trees']
    },
    preventionTips: ['Mow grass short before winter', 'Remove mulch and debris from lawn edges', 'Protect tree bases with hardware cloth'],
    diagnosticTest: 'Look for 1-2 inch wide worn pathways through the grass. Place an apple slice in the runway, cover it with an upturned pot or a propped shingle, and check after 24 hours: gnaw marks mean the runway is still active.'
  },

  'gophers': {
    id: 'gophers',
    name: 'Gophers',
    category: 'animal',
    damagePattern: 'Fan-shaped or crescent-shaped mounds of excavated soil; destroyed root zones',
    visualSigns: [
      'Fan-shaped or crescent mounds (not conical like mole mounds)',
      'Visible plugged holes on one side of mound',
      'Plants pulled underground or killed from below',
      'Soil mounds appear suddenly'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Bermudagrass', 'Perennial Ryegrass', 'Buffalograss'],
    commonRegions: ['Central', 'Southwest', 'Pacific', 'Great Plains'],
    severity: 'severe',
    activeSeasons: ['spring', 'summer', 'fall'],
    lifecycleInfo: 'Solitary and territorial. One gopher per burrow system. Active year-round but most visible in spring. Can move 200-300 pounds of soil per year.',
    controlOptions: {
      cultural: ['Compact soil to discourage tunneling', 'Install underground wire mesh barriers'],
      chemical: ['Strychnine-coated bait', 'Zinc phosphide bait placed in main tunnel'],
      organic: ['Macabee or Cinch traps placed in main tunnel', 'Exclusion with gopher wire below raised beds', 'Barn owl nest boxes to encourage predation']
    },
    preventionTips: ['Install gopher wire under new sod or gardens', 'Trap immediately when fresh mounds appear', 'Encourage natural predators'],
    diagnosticTest: 'Probe around a fresh mound to find the main tunnel (you\'ll feel the probe drop into the void). Mounds are fan-shaped with a visible soil plug, distinguishing them from mole mounds.'
  },

  'nematodes': {
    id: 'nematodes',
    name: 'Plant-Parasitic Nematodes',
    category: 'insect',
    damagePattern: 'Irregular yellowing and thinning with no clear pathogen; grass fails to respond to watering or fertilizer',
    visualSigns: [
      'Gradual decline with no obvious cause',
      'Yellowing and thinning that doesn\'t improve with care',
      'Stunted root systems when pulled',
      'Sandy soils are more susceptible'
    ],
    affectedGrassTypes: ['Bermudagrass', 'St. Augustinegrass', 'Zoysiagrass', 'Centipede'],
    commonRegions: ['Southeast', 'Gulf Coast', 'South Central'],
    severity: 'moderate',
    activeSeasons: ['summer'],
    lifecycleInfo: 'Microscopic roundworms living in soil. Multiple generations per year. Populations build up over years. Feed on roots, causing decline.',
    controlOptions: {
      cultural: ['Improve soil health with organic matter', 'Water deeply to maintain strong root system', 'Avoid sand-only topdressing'],
      chemical: ['Abamectin', 'Fluopyram (limited availability for residential use)'],
      organic: ['Add composted organic matter', 'Apply beneficial nematodes (predatory species)', 'Improve soil biology with compost tea']
    },
    preventionTips: ['Build healthy soil with organic amendments', 'Maintain strong root system through proper care', 'Avoid excessive sand topdressing'],
    diagnosticTest: 'Lab analysis required: collect 20+ soil cores from the edge of declining areas, mix, and submit to a nematode diagnostic lab. Visual diagnosis is not possible.'
  },

  'leafhoppers': {
    id: 'leafhoppers',
    name: 'Leafhoppers',
    category: 'insect',
    damagePattern: 'Stippled or whitened grass blades from feeding; overall lawn appears pale',
    visualSigns: [
      'Tiny wedge-shaped insects (1/8 inch) that hop when disturbed',
      'White stippling on grass blades',
      'Insects hop sideways away from footsteps',
      'Cast skins on grass blades'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['All regions'],
    severity: 'mild',
    activeSeasons: ['summer'],
    lifecycleInfo: 'Multiple generations per year. Adults overwinter in plant debris. Feed by piercing grass blades and sucking sap.',
    controlOptions: {
      cultural: ['Maintain healthy turf that can tolerate feeding', 'Reduce thatch', 'Mow at proper height'],
      chemical: ['Bifenthrin', 'Carbaryl', 'Lambda-cyhalothrin'],
      organic: ['Insecticidal soap', 'Neem oil', 'Encourage natural predators']
    },
    preventionTips: ['Usually not severe enough to warrant treatment', 'Maintain healthy turf', 'Reduce thatch buildup']
  },

  'spittlebugs': {
    id: 'spittlebugs',
    name: 'Spittlebugs',
    category: 'insect',
    damagePattern: 'Patches of white frothy "spittle" masses on grass stems; heavy feeding causes wilting',
    visualSigns: [
      'White frothy masses (spittle) on grass stems near soil level',
      'Small yellow-green nymphs hidden inside spittle',
      'Wilting and purpling of grass in severe infestations',
      'Adult froghoppers (small brown jumping insects)'
    ],
    affectedGrassTypes: ['Bermudagrass', 'Centipede', 'Bahiagrass', 'St. Augustinegrass'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast'],
    severity: 'mild',
    activeSeasons: ['spring', 'summer'],
    lifecycleInfo: 'One to two generations per year. Nymphs create spittle for protection. Adults are strong jumpers called froghoppers.',
    controlOptions: {
      cultural: ['Reduce thatch (nymphs live in thatch)', 'Mow to remove spittle masses', 'Water in the morning'],
      chemical: ['Bifenthrin', 'Carbaryl'],
      organic: ['Wash away spittle with strong water spray', 'Insecticidal soap', 'Reduce thatch habitat']
    },
    preventionTips: ['Keep thatch below 1/2 inch', 'Rarely severe enough for treatment', 'Maintain healthy turf']
  },

  'crane-flies': {
    id: 'crane-flies',
    name: 'Crane Flies (Leatherjackets)',
    category: 'insect',
    damagePattern: 'Irregular thin or bare patches in spring; larvae feed on grass roots and crowns',
    visualSigns: [
      'Large mosquito-like adults (legs span 2+ inches) in fall',
      'Grayish-brown larvae (leatherjackets) 1-1.5 inches in soil',
      'Thin turf in spring that doesn\'t recover',
      'Birds probing the soil for larvae'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Tall Fescue'],
    commonRegions: ['Pacific Northwest', 'Northeast', 'Great Lakes'],
    severity: 'moderate',
    activeSeasons: ['spring'],
    lifecycleInfo: 'One generation per year. Adults emerge in fall, lay eggs in turf. Larvae feed through winter and spring. Damage is most visible in March-April.',
    controlOptions: {
      cultural: ['Overseed thin areas in fall', 'Promote vigorous spring growth with fertilizer', 'Roll lawn to compress soil'],
      chemical: ['Chlorantraniliprole (fall application)', 'Imidacloprid'],
      organic: ['Beneficial nematodes (Steinernema feltiae) applied in fall', 'Promote predatory birds']
    },
    preventionTips: ['Monitor for adult flights in September-October', 'Apply treatment in fall if high populations expected', 'Maintain vigorous turf'],
    diagnosticTest: 'Cut a 6-inch square of turf in a declining area and turn it over. Count leatherjackets (gray-brown, tough-skinned, legless larvae). Threshold: 25+ per sq ft.'
  },

  'bermudagrass-mites': {
    id: 'bermudagrass-mites',
    name: 'Bermudagrass Mites',
    category: 'insect',
    damagePattern: 'Tufted, rosette growth ("witches broom") on bermudagrass; stunted internodes',
    visualSigns: [
      'Clumped tufted growth resembling tiny rosettes',
      'Shortened internodes giving a "witches broom" appearance',
      'Yellowing and thinning of affected areas',
      'Mites are microscopic; damage pattern is the identifier'
    ],
    affectedGrassTypes: ['Bermudagrass'],
    commonRegions: ['Southeast', 'South Central', 'Southwest'],
    severity: 'moderate',
    activeSeasons: ['spring', 'summer'],
    lifecycleInfo: 'Multiple generations per year. Mites are eriophyid type: worm-shaped and microscopic. They feed inside leaf sheaths, causing stunted growth.',
    controlOptions: {
      cultural: ['Scalp-mow affected areas and remove clippings', 'Fertilize to promote new growth', 'Irrigate well after treatment'],
      chemical: ['Abamectin', 'Dicofol (limited availability)'],
      organic: ['Scalp mowing to remove infested tissue', 'Promote vigorous regrowth with compost']
    },
    preventionTips: ['Monitor for early signs of tufting', 'Maintain vigorous bermudagrass health', 'Treat early before widespread damage'],
    diagnosticTest: 'Look for characteristic "witches broom" tufting. Confirmation requires microscopic examination of mites from leaf sheaths, but the damage pattern is diagnostic.'
  },

  'ants': {
    id: 'ants',
    name: 'Ants (Lawn-Dwelling Species)',
    category: 'insect',
    damagePattern: 'Small soil mounds that smother grass; mostly cosmetic damage',
    visualSigns: [
      'Small granular mounds of excavated soil on grass surface',
      'Ant trails visible on sidewalks and between mounds',
      'Grass smothered under mounds may yellow',
      'Mound activity greatest during wet spring weather'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['All regions'],
    severity: 'mild',
    activeSeasons: ['spring', 'summer', 'fall'],
    lifecycleInfo: 'Colonies hold an egg-laying queen and sterile female workers, with winged reproductives emerging in early spring and late summer to start new colonies. Common lawn species are cornfield ants, pavement ants, little black ants, and field ants (Formica). Mound activity is greatest during wet spring weather.',
    controlOptions: {
      cultural: ['Rake or wash mounds flat with a forceful stream from a garden hose', 'Flood the mound repeatedly, every few days, until the colony relocates'],
      chemical: ['Pyrethroids such as bifenthrin, applied per label', 'Carbaryl, applied per label', 'Fipronil gel bait'],
      organic: ['Spinosad product labeled for lawn and turf ants']
    },
    preventionTips: ['Most lawn ant species are beneficial and rarely require treatment', 'Treat only if mounds are smothering turf or catching the mower', 'Control food sources near the lawn'],
    diagnosticTest: 'Look straight down into the top of the pile. A soil-nesting lawn ant mound has an open hole in the middle, made of fine, loose soil pushed up between the grass blades. A fire ant mound has no central opening, so an open hole rules fire ants out.'
  },

  'scale-insects': {
    id: 'scale-insects',
    name: 'Ground Pearls and Scale Insects',
    category: 'insect',
    damagePattern: 'Slow decline of turf over years; yellowish pearls attached to roots',
    visualSigns: [
      'Gradual thinning with no obvious cause',
      'Round, pearl-like nodules (1/16 inch) on roots',
      'Centipedegrass and bermudagrass most affected',
      'No response to fertilizer or watering'
    ],
    affectedGrassTypes: ['Centipede', 'Bermudagrass', 'St. Augustinegrass', 'Zoysiagrass'],
    commonRegions: ['Southeast', 'Gulf Coast'],
    severity: 'moderate',
    activeSeasons: ['year-round'],
    lifecycleInfo: 'Ground pearls can remain in dormant cyst stage for years in soil. Very slow life cycle. No effective chemical controls available.',
    controlOptions: {
      cultural: ['Maintain optimal turf health', 'Irrigate during drought', 'Fertilize properly', 'Minimize other stress factors'],
      chemical: ['No effective chemical controls currently available'],
      organic: ['Improve soil health', 'Reduce other stress factors', 'Consider re-sodding with tolerant variety']
    },
    preventionTips: ['No proven prevention', 'Maintain optimal turf health to tolerate feeding', 'Choose tolerant grass varieties'],
    diagnosticTest: 'Dig up declining grass and examine roots with a hand lens. Look for round, pearl-like cysts (yellowish-white to pinkish) attached to roots.'
  }
}

// Helper: get pest by ID
export function getPestById(id: string): PestCharacteristics | undefined {
  return PEST_CHARACTERISTICS[id]
}

// Helper: find pest by name (case-insensitive partial match)
export function findPestByName(name: string): PestCharacteristics | undefined {
  const lower = name.toLowerCase()
  return Object.values(PEST_CHARACTERISTICS).find(p =>
    p.name.toLowerCase().includes(lower) || p.id.includes(lower.replace(/\s+/g, '-'))
  )
}

// Helper: get pests by grass type
export function getPestsByGrassType(grassType: string): PestCharacteristics[] {
  return Object.values(PEST_CHARACTERISTICS).filter(p =>
    p.affectedGrassTypes.some(g => g.toLowerCase().includes(grassType.toLowerCase()))
  )
}

// Helper: get pests by category
export function getPestsByCategory(category: 'insect' | 'animal'): PestCharacteristics[] {
  return Object.values(PEST_CHARACTERISTICS).filter(p => p.category === category)
}
